import { useCallback, useEffect, useRef, useState } from 'react';

const idleState = (scope) => ({ scope, phase: 'idle', seconds: 0, draft: null, error: null });
const stopTracks = (stream) => stream?.getTracks().forEach((track) => track.stop());

// Capture locale uniquement. Aucun callback d'envoi, upload ou API dans ce hook.
export function useVoiceRecorder(scopeId, maxSeconds = null) {
    const scopeRef = useRef(scopeId);
    scopeRef.current = scopeId;
    const sessionRef = useRef(null);
    const urlRef = useRef(null);
    const [state, setState] = useState(() => idleState(scopeId));

    const dispose = useCallback(() => {
        const session = sessionRef.current;
        sessionRef.current = null; // Invalide aussi les permissions encore en attente.
        if (session) {
            clearInterval(session.timer);
            if (session.recorder?.state !== 'inactive') {
                try { session.recorder?.stop(); } catch { /* Déjà arrêté. */ }
            }
            stopTracks(session.stream);
        }
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
    }, []);

    const discard = useCallback(() => {
        dispose();
        setState(idleState(scopeRef.current));
    }, [dispose]);

    useEffect(() => {
        discard();
        return dispose;
    }, [scopeId, discard, dispose]);

    const stop = useCallback(() => {
        const session = sessionRef.current;
        if (!session || session.stopping || session.recorder?.state !== 'recording') return;
        session.stopping = true;
        session.stoppedAt = performance.now();
        clearInterval(session.timer);
        setState(previous => ({ ...previous, phase: 'stopping' }));
        session.recorder.stop();
        stopTracks(session.stream);
    }, []);

    const start = useCallback(async () => {
        if (sessionRef.current || !scopeRef.current) return;
        if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
            setState({ ...idleState(scopeRef.current), error: 'Enregistrement non pris en charge par ce navigateur.' });
            return;
        }
        const session = { scope: scopeRef.current, chunks: [] };
        sessionRef.current = session;
        const isCurrent = () => sessionRef.current === session && scopeRef.current === session.scope;
        const fail = (message) => {
            if (!isCurrent()) return;
            dispose();
            setState({ ...idleState(session.scope), error: message });
        };
        setState({ ...idleState(session.scope), phase: 'requesting' });
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            if (!isCurrent()) { stopTracks(stream); return; }
            session.stream = stream;
            const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg', 'audio/mp4']
                .find(type => window.MediaRecorder.isTypeSupported?.(type));
            const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
            session.recorder = recorder;
            recorder.ondataavailable = (event) => {
                if (isCurrent() && event.data?.size) session.chunks.push(event.data);
            };
            recorder.onerror = () => fail('Enregistrement interrompu. Veuillez réessayer.');
            recorder.onstop = () => {
                clearInterval(session.timer);
                stopTracks(stream);
                if (!isCurrent()) return;
                const type = recorder.mimeType || session.chunks[0]?.type || 'audio/webm';
                const blob = new Blob(session.chunks, { type });
                session.chunks = [];
                if (!blob.size) { fail('Aucun son enregistré. Veuillez réessayer.'); return; }
                const duration = Math.min((session.stoppedAt || performance.now()) - session.startedAt, maxSeconds ? maxSeconds * 1000 : Infinity) / 1000;
                const ext = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm';
                const file = new File([blob], `vocal-${Date.now()}.${ext}`, { type });
                urlRef.current = URL.createObjectURL(blob);
                setState({ scope: session.scope, phase: 'ready', seconds: Math.floor(duration), error: null,
                    draft: { file, url: urlRef.current, duration, limitReached: !!maxSeconds && duration >= maxSeconds } });
            };
            recorder.start();
            session.startedAt = performance.now();
            setState({ ...idleState(session.scope), phase: 'recording' });
            session.timer = setInterval(() => {
                if (!isCurrent() || session.stopping) return;
                const seconds = Math.floor((performance.now() - session.startedAt) / 1000);
                setState(previous => ({ ...previous, seconds }));
                if (maxSeconds && seconds >= maxSeconds) stop(); // Préécoute, JAMAIS envoi.
            }, 250);
        } catch {
            fail('Microphone inaccessible. Vérifiez son autorisation puis réessayez.');
        }
    }, [dispose, maxSeconds, stop]);

    return { ...(state.scope === scopeId ? state : idleState(scopeId)), start, stop, discard };
}